// Recursos FHIR que deja 008 al guardar además de las `Condition` de M (Fase 3, cierre U7). Mismo patrón que 002/003
// (`guardadoAnamnesis.ts`): las constantes vitales del bloque G crean `Observation` de Signos Vitales, el motivo de
// atención del bloque C va a `Encounter.reasonCode` y las alergias del bloque E a `AllergyIntolerance`. Todo se arma acá
// como función pura; `Formulario008.tsx` lo mete en la misma transacción que las `Condition`.
import type { AllergyIntolerance, Encounter, Observation, Patient, Reference } from '@medplum/fhirtypes';
import { CLINICAL_STATUS_ALERGIA_ACTIVA, esRespuestaAlergiaNegativa } from '../extractors';
import type { EstadoBloques008Ingreso } from './bloques008IngresoDatos';
import { observacionesDeToma, type TomaSignos } from './signosVitalesToma';

/** `Encounter.reasonCode` — el "Motivo de atención" del bloque C (nada si está vacío). */
export const motivoDe008 = (e: EstadoBloques008Ingreso): string => e.c.motivo.trim();

/**
 * Toma de constantes del bloque G. La fecha y hora son las del "Inicio de atención" (bloque C); si faltan o son inválidas,
 * `observacionesDeToma` usa el momento del guardado. Con "Sin constantes vitales" marcado no se crea nada. Temperatura no
 * existe en el papel de 008; glicemia capilar y perímetro cefálico no tienen LOINC verificado en el catálogo (quedan en el PDF).
 */
export function tomaDe008(e: EstadoBloques008Ingreso): TomaSignos | undefined {
  if (e.g.sinConstantesVitales) return undefined;
  return { fecha: e.c.fecha, hora: e.c.hora, presion: e.g.presionArterial, fc: e.g.pulso, fr: e.g.frecuenciaRespiratoria, spo2: e.g.pulsioximetria, peso: e.g.peso, talla: e.g.talla };
}

/**
 * Alergias: solo si la categoría "1. Alérgicos" del bloque E está marcada, y solo los renglones del "Describir" que empiezan
 * con el número 1 (el papel pide "describir señalando el número") — misma regla que `alergiasDe003`. Las negativas
 * ("ninguna", "no refiere"...) no crean nada.
 */
export function alergiasDe008(e: EstadoBloques008Ingreso): string[] {
  if (!e.e.categorias.alergicos) return [];
  return e.e.describir
    .split(/[\n;]+/)
    .map((linea) => linea.trim())
    .flatMap((linea) => {
      const m = /^(\d{1,2})\s*[.\-:)]?\s*(.+)$/.exec(linea);
      return m && m[1] === '1' && m[2]!.trim() && !esRespuestaAlergiaNegativa(m[2]!) ? [m[2]!.trim()] : [];
    });
}

export function observacionesDe008(e: EstadoBloques008Ingreso, subject: Reference<Patient>, encounter: Reference<Encounter> | undefined, ahoraIso: string): Observation[] {
  const toma = tomaDe008(e);
  return toma ? observacionesDeToma(toma, subject, encounter, ahoraIso) : [];
}

export function alergiasFhirDe008(e: EstadoBloques008Ingreso, subject: Reference<Patient>, ahoraIso: string): AllergyIntolerance[] {
  return alergiasDe008(e).map((texto) => ({ resourceType: 'AllergyIntolerance', patient: subject, clinicalStatus: CLINICAL_STATUS_ALERGIA_ACTIVA, code: { text: texto }, recordedDate: ahoraIso }));
}
